import { useCallback, useState } from 'react';
import type React from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { ClientRender } from '@change-corgi/core/react/ssr/render';

import { SetPageStateValue } from 'src/shared/pageState';

import {
	StarterDashboardV2BreakpointStateProvider,
	useStarterDashboardV2PageNotificationManagerContext,
} from 'src/app/pages/starterDashboardV2/pageContext';
import { DASHBOARD_BOTTOM_NAV_HEIGHT_PX } from 'src/app/pages/starterDashboardV2/shared/constants';
import { AppDownloadBottomBanner } from 'src/app/shared/components/banners/appDownload';
import { AppDownloadBottomBannerV2 } from 'src/app/shared/components/banners/appDownloadV2';

import { PageBody } from '../components/PageBody';
import { PageHeader } from '../components/PageHeader';

import { usePageStateValue } from './hooks/usePageStateValue';
import { useTrackPageView } from './hooks/useTrackPageView';

export const Layout: React.FC = () => {
	const { section } = useKnownAvailableParams();

	useTrackPageView(section);
	const {
		petition,
		ownPetition,
		mobileAppDownloadBannerEnabled,
		mobileAppDownloadAndroidBannerEnabled,
		iOSBannerV2Enabled,
		androidBannerV2Enabled,
		desktopBannerV2Enabled,
	} = usePageStateValue();
	const {
		data: { notification },
	} = useStarterDashboardV2PageNotificationManagerContext();

	// Track if inline header is in viewport (starts true on desktop)
	// This controls whether the sticky PageHeader should be hidden on desktop
	const [isInlineHeaderInView, setIsInlineHeaderInView] = useState(true);

	// Callback for InlinePageHeader to report visibility changes
	const handleInlineHeaderVisibilityChange = useCallback((visible: boolean) => {
		setIsInlineHeaderInView(visible);
	}, []);

	// Only hide the sticky header on desktop when on the home section AND inline header is visible
	// For all other sections, always show the sticky header
	const isHomeSection = !section || section === 'home';
	const shouldHideOnDesktop = isHomeSection && isInlineHeaderInView;

	// We depend on client breakpoints
	return (
		<>
			<SetPageStateValue name="petition" value={petition} />
			<ClientRender placeholder={<div className="h-16" />}>
				<StarterDashboardV2BreakpointStateProvider>
					<PageHeader hideOnDesktop={shouldHideOnDesktop} />
					<div className="bg-fill-inverse-strong h-full w-full">
						{notification?.content}
						<PageBody onInlineHeaderVisibilityChange={handleInlineHeaderVisibilityChange} />
					</div>
				</StarterDashboardV2BreakpointStateProvider>
			</ClientRender>
			<AppDownloadBottomBanner
				sourceLocation="petition_dashboard"
				ownPetition={ownPetition}
				enabled={mobileAppDownloadBannerEnabled}
				androidEnabled={mobileAppDownloadAndroidBannerEnabled}
			/>
			<AppDownloadBottomBannerV2
				sourceLocation="petition_dashboard"
				ownPetition={ownPetition}
				iOSBannerEnabled={iOSBannerV2Enabled}
				androidBannerEnabled={androidBannerV2Enabled}
				desktopBannerEnabled={desktopBannerV2Enabled}
				bottomOffsetPx={DASHBOARD_BOTTOM_NAV_HEIGHT_PX}
			/>
		</>
	);
};
